/**
 * Authentication Service
 *
 * Auth lives entirely in an HttpOnly session cookie set by the API. The SPA
 * never reads or stores the raw JWT — it only caches the user/session
 * envelope returned by /api/session so the UI can render before the next
 * server round-trip validates the cookie.
 */
import apiService from './apiService';

class AuthService {
  constructor() {
    this.userKey = 'user_data';
    this.sessionKey = 'session_data';
    this.rememberMeKey = 'remember_me';

    // Legacy keys we still clean up on logout / clearAuth so users upgrading
    // from the bearer-token build don't end up with stale data.
    this.legacyTokenKey = 'access_token';

    // Session validation caching
    this.lastValidationTime = 0;
    this.lastValidationResult = null;
    this.validationCacheDuration = 30 * 1000; // 30 seconds
    this.pendingValidation = null; // For deduplication
  }
  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {boolean} rememberMe - Whether to persist login (default: true for better UX)
   * @returns {Promise<Object>} - Login response
   */
  async login(email, password, rememberMe = true) {
    try {
      const response = await apiService.post('/api/session', {
        email,
        password
      });

      if (response && response.user) {
        // The JWT is set as an HttpOnly cookie by the API; we only persist
        // user/session metadata for the SPA's own UI.
        this.setUser(response.user, rememberMe);
        this.setSessionData(response, rememberMe);

        // Set remember me preference
        localStorage.setItem(this.rememberMeKey, rememberMe ? 'true' : 'false');

        // Clear any previous validation cache since we have a new session
        this.lastValidationTime = 0;
        this.lastValidationResult = null;
        this.pendingValidation = null;

        return response;
      }

      // Pending-activation envelopes ({ result: "UserPending", message }) come back
      // as 200s without a user — pass them through so AuthContext can route to /verify-email.
      if (response && response.result) {
        return response;
      }

      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user by ending session
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      // Always call the server so the HttpOnly cookie gets cleared. The SPA
      // does not know its own session id in the cookie-only flow, so we use
      // the id=0 alias that resolves to "current session" server-side.
      const sessionData = this.getSessionData();
      const sessionId = sessionData?.sessionId ?? 0;
      await apiService.delete(`/api/session/${sessionId}`);
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Always clear local storage
      this.clearAuth();
    }
  }
  /**
   * Get stored user data
   * @returns {Object|null} - User data or null
   */
  getUser() {
    // First check localStorage (preferred), then sessionStorage for backward compatibility
    const userData = localStorage.getItem(this.userKey) || 
                    sessionStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }
  /**
   * Set user data in storage
   * @param {Object} user - User data
   * @param {boolean} persist - Whether to persist in localStorage (default: true)
   */
  setUser(user, persist = true) {
    const userJson = JSON.stringify(user);
    if (persist) {
      localStorage.setItem(this.userKey, userJson);
      // Remove from sessionStorage if it exists there
      sessionStorage.removeItem(this.userKey);
    } else {
      sessionStorage.setItem(this.userKey, userJson);
      // Remove from localStorage if it exists there
      localStorage.removeItem(this.userKey);
    }
  }
  /**
   * Get stored session data
   * @returns {Object|null} - Session data or null
   */
  getSessionData() {
    // First check localStorage (preferred), then sessionStorage for backward compatibility
    const sessionData = localStorage.getItem(this.sessionKey) || 
                       sessionStorage.getItem(this.sessionKey);
    return sessionData ? JSON.parse(sessionData) : null;
  }
  /**
   * Set session data in storage
   * @param {Object} session - Session data
   * @param {boolean} persist - Whether to persist in localStorage (default: true)
   */
  setSessionData(session, persist = true) {
    const sessionJson = JSON.stringify(session);
    if (persist) {
      localStorage.setItem(this.sessionKey, sessionJson);
      // Remove from sessionStorage if it exists there
      sessionStorage.removeItem(this.sessionKey);
    } else {
      sessionStorage.setItem(this.sessionKey, sessionJson);
      // Remove from localStorage if it exists there
      localStorage.removeItem(this.sessionKey);
    }
  }

  /**
   * Check if user appears authenticated (locally).
   *
   * The authoritative answer comes from the API via validateSession() — this
   * method only reflects whether we have a cached user record, which is the
   * SPA's signal to render an authenticated UI before validation completes.
   * The actual JWT lives in an HttpOnly cookie that JS cannot inspect.
   *
   * @returns {boolean}
   */
  isAuthenticated() {
    return this.getUser() !== null;
  }

  /**
   * Get current user sessions
   * @returns {Promise<Array>} - Array of user sessions
   */
  async getSessions() {
    try {
      return await apiService.get('/api/session');
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }

  /**
   * End a specific session
   * @param {number} sessionId - Session ID to end
   * @returns {Promise<void>}
   */
  async endSession(sessionId) {
    try {
      await apiService.delete(`/api/session/${sessionId}`);
    } catch (error) {
      console.error('Error ending session:', error);
      throw error;
    }
  }

  /**
   * Validate current session by calling /api/session/0
   * This checks if the session stored in localStorage is still valid
   * @returns {Promise<boolean>} - True if session is valid, false otherwise
   */
  async validateSession() {
    // Check cache first
    const now = Date.now();
    if (this.lastValidationResult !== null && 
        (now - this.lastValidationTime) < this.validationCacheDuration) {
      // console.log('Returning cached validation result:', this.lastValidationResult);
      return this.lastValidationResult;
    }

    // Avoid duplicate validations
    if (this.pendingValidation) {
      // console.log('Validation in progress, waiting for result');
      return this.pendingValidation;
    }

    this.pendingValidation = (async () => {
      try {
        const sessionData = this.getSessionData();
        
        // If no session data is stored, return false
        if (!sessionData || !sessionData.sessionId) {
          // console.log('No session data found, session is invalid');
          this.lastValidationResult = false;
          return false;
        }

        // console.log('Validating session with API call to /api/session/0');
        
        // Call the API to validate the session with sessionId 0
        // This special endpoint validates the current session
        const response = await apiService.get('/api/session/0');
        
        // If we get a valid response, the session is still active
        if (response && response.id) {
          // console.log('Session validation successful');
          this.lastValidationResult = true;
          return true;
        }
        
        // console.log('Session validation failed - no valid response');
        this.lastValidationResult = false;
        return false;
      } catch (error) {
        console.error('Session validation error:', error);
        
        // If we get a 401/403 error, the session is definitely invalid
        if (error.status === 401 || error.status === 403) {
          // console.log('Session validation failed - authentication error (401/403)');
          this.lastValidationResult = false;
          return false;
        }
        
        // Check for network errors or other HTTP errors that indicate session issues
        if (error.status >= 400 && error.status < 500) {
          // console.log(`Session validation failed - client error (${error.status})`);
          this.lastValidationResult = false;
          return false;
        }
        
        // For server errors (5xx) or network issues, we'll assume the session might still be valid
        // to avoid false negatives due to temporary server/network issues
        // console.log('Session validation error - assuming session might still be valid due to server/network issues');
        this.lastValidationResult = true;
        return true;
      } finally {
        this.lastValidationTime = Date.now();
        this.pendingValidation = null;
      }
    })();

    return this.pendingValidation;
  }

  /**
   * Clear all authentication data
   */
  clearAuth() {
    // Clear from both storage types
    [this.userKey, this.sessionKey, this.legacyTokenKey].forEach(key => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
    localStorage.removeItem(this.rememberMeKey);
    
    // Clear session validation cache
    this.lastValidationTime = 0;
    this.lastValidationResult = null;
    this.pendingValidation = null;
    // console.log('Authentication data and validation cache cleared');
  }

  /**
   * Clear session validation cache only
   */
  clearValidationCache() {
    this.lastValidationTime = 0;
    this.lastValidationResult = null;
    this.pendingValidation = null;
    // console.log('Session validation cache cleared');
  }

  /**
   * Get validation cache info for debugging
   */
  getValidationCacheInfo() {
    return {
      hasCache: this.lastValidationResult !== null,
      cacheTime: this.lastValidationTime,
      cacheAge: this.lastValidationTime ? Date.now() - this.lastValidationTime : 0,
      cacheResult: this.lastValidationResult,
      hasPendingValidation: this.pendingValidation !== null
    };
  }

  /**
   * Check if remember me was selected
   * @returns {boolean}
   */
  isRememberMeEnabled() {
    const rememberMe = localStorage.getItem(this.rememberMeKey);
    // Default to true if not set (new behavior for better UX)
    return rememberMe === null ? true : rememberMe === 'true';
  }

  /**
   * Refresh token if needed
   * @returns {Promise<boolean>} - True if token is valid/refreshed
   */
  async refreshTokenIfNeeded() {
    if (!this.isAuthenticated()) {
      return false;
    }

    // In a real implementation, you might want to refresh the token
    // if it's close to expiry. For now, we just validate it exists.
    return true;
  }

  /**
   * Register a new user account (step 1 of 2-step registration)
   * @param {string} email - User email
   * @param {string} referralCode - Optional referral code
   * @param {boolean} receivePromotions - Whether user wants to receive promotional emails (default: true)
   * @returns {Promise<Object>} - Registration response
   */
  async register(email, referralCode = null, receivePromotions = true, firstName = null, lastName = null, title = null, password = null) {
    try {
      const payload = { 
        email,
        receivePromotions: receivePromotions
      };
      
      if (referralCode) payload.referralCode = referralCode;
      if (firstName) payload.firstName = firstName;
      if (lastName) payload.lastName = lastName;
      if (title) payload.title = title;
      if (password) payload.password = password;

      const response = await apiService.post('/api/useraccount/register', payload);

      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Complete user registration (step 2 of 2-step registration)
   * @param {string} token - Activation token from email
   * @param {Object} userData - User data (firstName, lastName, password, title)
   * @returns {Promise<Object>} - Registration completion response
   */
  async confirmRegistration(token, userData) {
    try {
      const payload = { token };
      // Include user data fields if provided (for link-based flow they come from the form;
      // for 6-digit code flow the backend uses data stored during registration)
      if (userData.firstName) payload.firstName = userData.firstName;
      if (userData.lastName) payload.lastName = userData.lastName;
      if (userData.password) payload.password = userData.password;
      if (userData.title) payload.title = userData.title;
      if (userData.email) payload.email = userData.email;
      const response = await apiService.post('/api/useraccount/confirm-registration', payload);

      return response;
    } catch (error) {
      console.error('Registration confirmation error:', error);
      throw error;
    }
  }

  /**
   * Request password reset link (step 1 of 2-step password reset)
   * @param {string} email - User email
   * @returns {Promise<Object>} - Password reset response
   */
  async forgotPassword(email) {
    try {
      const response = await apiService.post('/api/useraccount/forgot-password', {
        email
      });

      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  /**
   * Reset password using token (step 2 of 2-step password reset)
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Password reset response
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await apiService.post('/api/useraccount/reset-password', {
        token,
        newPassword
      });

      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  /**
   * Resend activation email
   * @param {string} email - User email
   * @returns {Promise<Object>} - Resend activation response
   */
  async resendActivationEmail(email) {
    try {
      const response = await apiService.post('/api/useraccount/resend-registration-email', {
        email
      });

      return response;
    } catch (error) {
      console.error('Resend activation email error:', error);
      throw error;
    }
  }

  /**
   * Change user password
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Password change response
   */
  async changePassword(oldPassword, newPassword) {
    try {
      const response = await apiService.post('/api/useraccount/change-password', {
        oldPassword,
        newPassword
      });

      return response;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  /**
   * Update user profile information
   * @param {Object} profileData - Profile data to update
   * @param {string} profileData.firstName - First name
   * @param {string} profileData.lastName - Last name
   * @param {string} profileData.title - User title
   * @returns {Promise<Object>} - Updated user data
   */
  async updateProfile(profileData) {
    try {
      const currentUser = this.getUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        title: profileData.title
      };

      const response = await apiService.put('/api/useraccount', updateData);

      // Update stored user data with new information
      const updatedUser = { ...currentUser, ...response };
      this.setUser(updatedUser, this.isRememberMeEnabled());

      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from promotional emails using token
   * This is a public endpoint that doesn't require authentication
   * @param {string} token - Encrypted unsubscribe token from email
   * @returns {Promise<Object>} - Unsubscribe response with message
   */
  async unsubscribe(token) {
    console.log('[authService.unsubscribe] Starting unsubscribe with token:', token);
    try {
      console.log('[authService.unsubscribe] Calling apiService.get with endpoint /api/useraccount/unsubscribe and params:', { token });
      const response = await apiService.get(`/api/useraccount/unsubscribe`, { token });
      console.log('[authService.unsubscribe] API call successful, response:', response);
      return response;
    } catch (error) {
      console.error('[authService.unsubscribe] API call failed, error:', error);
      console.error('[authService.unsubscribe] Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        data: error.data
      });
      throw error;
    }
  }
}

// Expose authService to window for debugging in development
if (process.env.NODE_ENV === 'development') {
  window.authService = new AuthService();
}

export default new AuthService();
